/**
 * Earnings Queries (TanStack Query)
 * For creator affiliate earnings tracking (FirstPromoter integration)
 */

import { useQuery } from '@tanstack/react-query';
import {
  FIRSTPROMOTER_RATE_LIMITED,
  FirstPromoterUnavailableError,
  retryUnlessRateLimited,
} from '@/shared/lib/firstpromoter/rate-limit';
import {
  canRequestEarnings,
  useCreatorProgramStatus,
} from '@/shared/hooks/use-creator-program-status';
import type { EarningsData, DateRange, GroupBy } from '@/types';

/** Subtract days from a YYYY-MM-DD string in UTC */
function subtractDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split('T')[0]!;
}

/** Map DateRange to number of days */
const RANGE_DAYS: Record<string, number> = {
  '1d': 1,
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

/** Adjust groupBy to prevent too-granular or too-coarse grouping for a given range */
function adjustGroupBy(range: DateRange, groupBy: GroupBy): GroupBy {
  if (range === '1d') return 'day';
  if (range === '7d' && groupBy === 'month') return 'day';
  if (range === '90d' && groupBy === 'day') return 'week';
  if (range === '365d' && groupBy === 'day') return 'month';
  if (range === 'all') return 'month';
  return groupBy;
}

/**
 * Calculate date range parameters based on range selection.
 * All dates are computed in UTC to stay aligned with FirstPromoter's API.
 */
function getDateParams(range: DateRange, groupBy: GroupBy) {
  const todayUTC = new Date().toISOString().split('T')[0]!;
  const days = RANGE_DAYS[range];
  const startDate = range === 'all' ? '2020-01-01' : subtractDays(todayUTC, days ?? 30);

  return {
    start_date: startDate + 'T00:00:00.000Z',
    end_date: todayUTC + 'T23:59:59.999Z',
    group_by: adjustGroupBy(range, groupBy),
  };
}

/**
 * Calculate date range for the prior equivalent period.
 * E.g. for "30d", returns the 30 days before the current 30-day window.
 */
function getPriorDateParams(range: DateRange, groupBy: GroupBy) {
  const days = RANGE_DAYS[range];
  if (!days) return null; // "all" has no prior period

  const todayUTC = new Date().toISOString().split('T')[0]!;
  const currentStart = subtractDays(todayUTC, days);
  const priorEnd = subtractDays(currentStart, 1);
  const priorStart = subtractDays(currentStart, days);

  return {
    start_date: priorStart + 'T00:00:00.000Z',
    end_date: priorEnd + 'T23:59:59.999Z',
    group_by: adjustGroupBy(range, groupBy),
  };
}

/**
 * Fetch earnings data from Next.js API route (proxy to FirstPromoter)
 */
async function fetchEarnings(range: DateRange, groupBy: GroupBy): Promise<EarningsData> {
  const params = getDateParams(range, groupBy);
  const searchParams = new URLSearchParams(params);

  const response = await fetch(`/api/creator-studio/earnings?${searchParams}`);

  if (!response.ok) {
    const result = await response.json();
    if (result.error === FIRSTPROMOTER_RATE_LIMITED) throw new FirstPromoterUnavailableError();
    throw new Error(result.error || 'Failed to fetch earnings data');
  }

  return response.json();
}

async function fetchPriorEarnings(range: DateRange, groupBy: GroupBy): Promise<EarningsData> {
  const params = getPriorDateParams(range, groupBy);
  if (!params) throw new Error('No prior period for this range');

  const searchParams = new URLSearchParams(params);
  const response = await fetch(`/api/creator-studio/earnings?${searchParams}`);

  if (!response.ok) {
    const result = await response.json();
    if (result.error === FIRSTPROMOTER_RATE_LIMITED) throw new FirstPromoterUnavailableError();
    throw new Error(result.error || 'Failed to fetch prior period data');
  }

  return response.json();
}

// NO_PROMOTER_ACCOUNT is a structural answer (channel not in the Creator Program yet): a retry
// can only repeat it.
function retryEarnings(failureCount: number, error: unknown): boolean {
  if (error instanceof Error && error.message.includes('NO_PROMOTER_ACCOUNT')) return false;
  return retryUnlessRateLimited(failureCount, error);
}

/**
 * Hook to get earnings data with caching. Disabled while `/me` says the channel is not in the
 * Creator Program — a channel without a FirstPromoter id can only get NO_PROMOTER_ACCOUNT back.
 */
export function useEarnings(range: DateRange, groupBy: GroupBy) {
  const programActive = canRequestEarnings(useCreatorProgramStatus());
  return useQuery({
    queryKey: ['earnings', range, groupBy],
    queryFn: () => fetchEarnings(range, groupBy),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: retryEarnings,
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    enabled: programActive,
  });
}

/**
 * Hook to get prior period earnings for comparison
 */
export function usePriorPeriodEarnings(range: DateRange, groupBy: GroupBy) {
  const programActive = canRequestEarnings(useCreatorProgramStatus());
  return useQuery({
    queryKey: ['earnings', 'prior', range, groupBy],
    queryFn: () => fetchPriorEarnings(range, groupBy),
    staleTime: 5 * 60 * 1000,
    retry: retryEarnings,
    gcTime: 10 * 60 * 1000,
    enabled: programActive && range !== 'all',
  });
}
